import React, { useCallback, useEffect } from "react";
import { GoogleMap, InfoWindow, Marker, useJsApiLoader } from '@react-google-maps/api'
import { appConfig } from "../common/config";
import { Descriptions } from "antd";
import { Link } from "react-router-dom";
import { formatDateTime } from "../common/utils";

const containerStyle = {
  width: "100%",
  height: "600px",
};

export interface ICustomizedGoogleMapMultiProps {
  devices: any[];
  focusPosition?: any;
}

const CustomizedGoogleMapMulti: React.FC<ICustomizedGoogleMapMultiProps> = ({
  devices,
  focusPosition,
}) => {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: appConfig.settings.googleMapApiKey,
  });

  const [selectedPosition, setSelectedPosition] = React.useState<any>(null);
  const mapRef = React.useRef<any>(null);

  const handleMarkerClick = useCallback((pos: any) => {
    setSelectedPosition(pos);
  }, []);

  // Adjust map scale to fit all positions
  const handlePositions = useCallback((map: any, pts: any[]) => {
    if (!map || !pts || pts.length === 0) return;
    var latlngbounds = new google.maps.LatLngBounds();
    pts.forEach(p => {
      latlngbounds.extend(new google.maps.LatLng(p.coord.lat, p.coord.lng));
    });
    map.fitBounds(latlngbounds);
    window.google.maps.event.addListenerOnce(map, "idle", () => {
      //if (map.getZoom() > 15) map.setZoom(15);
    });
  }, []);

  // Triggered when the map is loaded
  const onLoad = useCallback((map: any) => {
    mapRef.current = map;
    handlePositions(map, devices);
  }, [handlePositions, devices]);

  // Triggered when positions change
  useEffect(() => {
    if (mapRef.current) {
      handlePositions(mapRef.current, devices);
    }
  }, [handlePositions, devices]);

  // Triggered when focusPosition changes
  useEffect(() => {
    if (mapRef.current && focusPosition) {
      mapRef.current.panTo(focusPosition.coord);
      mapRef.current.setZoom(15);
    }
  }, [focusPosition]);

  const centerPos = devices.length > 0
    ? devices[0].coord
    : appConfig.settings.mapDefaultCoordinate;

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={centerPos}
      zoom={15}
      onClick={(e) => {
      }}
      onLoad={onLoad}
    >
      {
        devices?.map((device, index) => {
          const markerPos = device.coord;
          if (!markerPos) return null;
          return (
            <Marker
              key={index}
              position={markerPos}
              title={device.SerialNumber}
              icon={{
                url: device.icon,
              }}
              onClick={() => handleMarkerClick(device)}
            />
          );
        })
      }
      {
        selectedPosition && (
          <InfoWindow
            position={selectedPosition.coord}
            options={{pixelOffset: new google.maps.Size(0, -30)}}
            onCloseClick={() => setSelectedPosition(null)}
          >
            <div style={{ width: 300 }}>
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item
                  label="Serial"
                  styles={{ label: { padding: 2 }, content: { padding: 2 } }}
                >
                  <Link to={`/events/searchType/Device ID or Serial/keywords/${selectedPosition.SerialNumber}`}>
                    {selectedPosition.SerialNumber}
                  </Link>
                </Descriptions.Item>
                <Descriptions.Item
                  label="Name"
                  styles={{ label: { padding: 2 }, content: { padding: 2 } }}
                >
                  {selectedPosition.InstallationData?.AdditionalData?.Name}
                </Descriptions.Item>
                {
                  selectedPosition.InstallationData?.AdditionalData?.Address && (
                    <Descriptions.Item
                      label="Address"
                      styles={{ label: { padding: 2 }, content: { padding: 2 } }}
                    >
                      {selectedPosition.InstallationData?.AdditionalData?.Address}
                    </Descriptions.Item>
                  )
                }
                <Descriptions.Item
                  label="Last Received"
                  styles={{ label: { padding: 2 }, content: { padding: 2 } }}
                >
                  {formatDateTime(selectedPosition.LastReceived)}
                </Descriptions.Item>
                {
                  selectedPosition.level !== undefined && selectedPosition.level !== null && (
                    <Descriptions.Item
                      label="Bin Level"
                      styles={{ label: { padding: 2 }, content: { padding: 2 } }}
                    >
                      {selectedPosition.level}%
                    </Descriptions.Item>
                  )
                }
                {
                  (selectedPosition.level === undefined || selectedPosition.level === null) && selectedPosition.ultrasonicDistance !== undefined && (
                    <Descriptions.Item
                      label="Ultrasonic Distance"
                      styles={{ label: { padding: 2 }, content: { padding: 2 } }}
                    >
                      {selectedPosition.ultrasonicDistance} cm
                    </Descriptions.Item>
                  )
                }
              </Descriptions>
            </div>
          </InfoWindow>
        )
      }
  </GoogleMap>
  ) : <>Loading Google Map</>;
};

export default CustomizedGoogleMapMulti;